import dayjs from 'dayjs';

import type { Reading } from './liveModel';
import { numeric } from './liveModel';

export const LEGACY_WINDOW = { from: '2025-08-22', to: '2025-08-25' };
export const LEGACY_REGIONS = [
  'Global - All Markets',
  'AU',
  'UK',
  'US + CA',
  'NZ',
  'SG',
  'Asia + ME',
  'DE',
  'EU',
  'FR',
  'IN',
  'ROW',
  'HK',
  'ZA',
];

/** Parameter 11 drives this historical calculation independently of the row's region. No ELSE in Tableau. */
export function legacyOldRate(region: string): number | null {
  if (region === 'UK') return 0.021;
  if (['US + CA', 'NZ', 'SG', 'ROW'].includes(region)) return 0.0292;
  if (['AU', 'EU', 'IN', 'Asia + ME', 'Global - All Markets'].includes(region)) return 0.011;
  return null;
}

/** Original Net ROAS uses one as the denominator only when observed spend is exactly zero. */
export function financialReadings(
  rows: Array<Reading>,
  legacyRegion = 'Global - All Markets',
  boundary: 'overview' | 'chart-current' = 'overview',
): Array<Reading> {
  return rows.map((row) => {
    const next = { ...row };
    // Warehouse NUMERIC/BIGNUMERIC transport may be a numeric string. Only governed financial measures are coerced.
    for (const key of [
      'gross_sales_aud_br',
      'gross_sales_aud_ar',
      'credit_sales_aud',
      'cash_sales_aud',
      'airline_sales_aud',
      'promo_sales_aud',
      'voucher_sales_aud',
      'service_fee',
      'customer_discount',
      'cash_sales_aud_ar',
      'margin_aud_br',
      'margin_aud_ar',
      'margin_luxplus_aud',
      'cash_refund_aud',
      'credit_refund_aud',
      'airline_refund_aud',
      'promo_refund_aud',
      'voucher_refund_aud',
      'total_refund_aud',
      'new_customers',
      'new_customer_gm_br',
      'new_customer_gm_ar',
      'cost_aud',
      'interest_income',
      'vcc_rebate',
      'bank_fees',
      'commission',
      'gross_cm',
      'comparison_cm',
      'net_cm',
      'net_margin',
      'bank_fees_legacy_copy',
      'pmax',
      'affiliates',
      'apple_search_ads',
      'velocity_spend',
      'manual_marketing_spend',
      'source_row_count',
    ]) {
      if (key in row) next[key] = numeric(row[key]);
    }

    if ('margin_aud_ar' in row && 'gross_sales_aud_ar' in row) {
      const gm = numeric(row.margin_aud_ar) ?? 0,
        ttv = numeric(row.gross_sales_aud_ar) ?? 0;
      next.gm_rate = ttv === 0 ? 0 : gm / ttv;
    }
    if ('total_refund_aud' in row && 'gross_sales_aud_br' in row) {
      const refund = numeric(row.total_refund_aud),
        gross = numeric(row.gross_sales_aud_br);
      next.refund_rate = refund === null || gross === null || gross === 0 ? 0 : refund / gross;
    }
    if ('bank_fees' in row)
      next.bank_fees_alert = (numeric(row.bank_fees) ?? 0) >= 1000000 ? 'Bank fees above 1M' : 'Bank fees ok';
    const operands = [
      'margin_aud_ar',
      'gross_sales_aud_ar',
      'bank_fees',
      'customer_discount',
      'cost_aud',
      'interest_income',
      'vcc_rebate',
    ];
    if (operands.every((key) => key in row)) {
      const gm = numeric(row.margin_aud_ar) ?? 0;
      const ttv = numeric(row.gross_sales_aud_ar) ?? 0;
      const fees = numeric(row.bank_fees);
      const discounts = numeric(row.customer_discount) ?? 0;
      const cost = numeric(row.cost_aud);
      const interest = numeric(row.interest_income);
      const vcc = numeric(row.vcc_rebate);
      next.commission = ttv * 0.0077;
      // Matrix Net Margin wraps the entire arithmetic expression, while comparison wraps each SUM.
      next.net_margin =
        fees === null || interest === null || vcc === null ? 0 : gm - ttv * 0.0077 - fees - discounts + interest + vcc;
      next.net_cm = cost === null ? null : Number(next.net_margin) - cost;
      next.gross_cm = cost === null ? null : gm - cost;
      next.comparison_cm =
        boundary === 'chart-current' &&
        [row.gross_sales_aud_ar, row.bank_fees, row.customer_discount, row.cost_aud].some(
          (value) => numeric(value) === null,
        )
          ? null
          : gm - (fees ?? 0) - ttv * 0.0077 - discounts - (cost ?? 0);
    }

    const spend = numeric(row.cost_aud);
    for (const [key, operand] of [
      ['margin_roas', 'margin_aud_ar'],
      ['ttv_roas', 'gross_sales_aud_ar'],
    ]) {
      if (!(operand in row)) continue;
      const value = numeric(row[operand]) ?? 0;
      next[key] = spend === null ? null : value / (spend === 0 ? 1 : spend);
    }
    const cash = numeric(row.cash_sales_aud_ar);
    const rate = legacyOldRate(legacyRegion);
    next.bank_fees_legacy_old = cash === null || rate === null ? null : cash * rate;
    return next;
  });
}

/** Sheet 68 includes the workbook MTD operands even though its observed dates are fixed in 2025. */
export function legacyReadings(rows: Array<Reading>, region: string, asOf = new Date()): Array<Reading> {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(asOf);
  const yesterday = dayjs(today).subtract(1, 'day');
  const factor = yesterday.daysInMonth() / yesterday.date();
  return financialReadings(rows, region).map((row) => ({
    ...row,
    bank_fees_mtd: numeric(row.bank_fees) === null ? null : Number(row.bank_fees) * factor,
    commission_mtd: numeric(row.commission) === null ? null : Number(row.commission) * factor,
  }));
}

/** Original MTD sheets filter from the start of yesterday's month through yesterday. */
export function completedFinancialMonth(asOf = new Date()) {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Australia/Sydney',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(asOf);
  const yesterday = dayjs(today).subtract(1, 'day');
  return { from: yesterday.startOf('month').format('YYYY-MM-DD'), to: yesterday.format('YYYY-MM-DD') };
}

export function financialMonthExpectation(rows: Array<Reading>, from: string, to: string): Array<Reading> {
  if (from.slice(0, 7) !== to.slice(0, 7) || !from.endsWith('-01')) return [];
  const factor = dayjs(to).daysInMonth() / dayjs(to).date();
  const groups = new Map<string, Reading>();
  const derivedRatios = new Set(['gm_rate', 'margin_roas', 'ttv_roas', 'refund_rate']);
  for (const row of rows) {
    const region = String(row.region ?? 'Unknown');
    const total = groups.get(region) ?? { region };
    for (const [key, raw] of Object.entries(row)) {
      if (key === 'region' || key.includes('date') || key === 'day' || key === 'month' || derivedRatios.has(key))
        continue;
      const value = numeric(raw);
      if (!(key in total)) total[key] = null;
      if (value !== null) total[key] = (numeric(total[key]) ?? 0) + value * factor;
    }
    groups.set(region, total);
  }
  return financialReadings([...groups.values()]).map((row) => {
    const gm = numeric(row.margin_aud_ar);
    const ttv = numeric(row.gross_sales_aud_ar);
    const spend = numeric(row.cost_aud);
    const refund = numeric(row.total_refund_aud);
    const br = numeric(row.gross_sales_aud_br);
    return {
      ...row,
      gm_rate: gm === null || ttv === null || ttv === 0 ? null : gm / ttv,
      margin_roas: gm === null || spend === null ? null : gm / (spend === 0 ? 1 : spend),
      // Refunds and gross sales are both projected above, so their ratio needs no further scaling. The
      // workbook multiplied the ratio by the projection factor too, overstating it (e.g. 15.1% read 23.5%).
      refund_rate: refund === null || br === null || br === 0 ? 0 : refund / br,
    };
  });
}
